import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { unwrap } from '@/api/envelope';
import { readArcVersion, type ArcVersion } from '@/api/generated';

import type { Arc } from './arcTypes';
import { arcVersionKey } from './keys';

/** What the version check answers, as a reply that carries the whole ARC says it. */
export function versionOf(arc: Arc): ArcVersion {
  return {
    draft_version: arc.draft_version,
    status: arc.status,
    published_at: arc.published_at ?? null,
    draft_saved_at: arc.draft_saved_at ?? null,
    draft_saved_by: arc.draft_saved_by ?? null,
  };
}

/** How often the open builder asks whether someone else saved (D25). */
export const VERSION_CHECK_MS = 15_000;

/**
 * The ARC's version, and who saved it when, asked for every 15 seconds while
 * the tab is visible and the ARC has loaded (arc-and-discovery-hardening,
 * D25, D26, B32). Hidden, it asks nothing; shown again, it asks once at once,
 * so a tab left in the background catches up the moment it is looked at.
 */
export function useArcVersion(organizationId: string | null, enabled: boolean) {
  const query = useQuery({
    queryKey: arcVersionKey(organizationId),
    queryFn: async () => {
      const reply = await readArcVersion({ throwOnError: true });
      return unwrap(reply.data);
    },
    enabled: enabled && organizationId !== null,
    refetchInterval: VERSION_CHECK_MS,
    refetchIntervalInBackground: false,
    // The visibility listener below asks instead, exactly once.
    refetchOnWindowFocus: false,
  });

  const { refetch } = query;
  const active = enabled && organizationId !== null;
  useEffect(() => {
    if (!active) return undefined;
    const shown = () => {
      if (document.visibilityState === 'visible') void refetch();
    };
    document.addEventListener('visibilitychange', shown);
    return () => document.removeEventListener('visibilitychange', shown);
  }, [active, refetch]);

  return query;
}
